import { useState, useEffect, useMemo } from 'react';
import { PublicFilterDigitalProduct } from '../../../Redux/digital-product/types';

interface DigitalProductFilterProps {
  onFilterChange: (filters: PublicFilterDigitalProduct) => void;
  currentFilters: PublicFilterDigitalProduct;
}

const DigitalProductFilter = ({ onFilterChange, currentFilters }: DigitalProductFilterProps) => {
  const [filters, setFilters] = useState<PublicFilterDigitalProduct>(currentFilters);

  useEffect(() => {
    setFilters(currentFilters);
  }, [currentFilters]);

  // Track active price filter
  const activePriceFilter = useMemo(() => {
    if (!filters.minPrice && !filters.maxPrice) return 'all';
    if (filters.maxPrice === 300 && !filters.minPrice) return 'under300';
    if (filters.minPrice === 300 && filters.maxPrice === 500) return '300-500';
    if (filters.minPrice === 500 && filters.maxPrice === 1000) return '500-1000';
    if (filters.minPrice === 1000 && filters.maxPrice === 2000) return '1000-2000';
    if (filters.minPrice === 2000 && !filters.maxPrice) return '2000Plus';
    return null;
  }, [filters.minPrice, filters.maxPrice]);

  const handlePriceFilter = (minPrice?: number, maxPrice?: number) => {
    const newFilters = {
      ...filters,
      minPrice,
      maxPrice,
      page: 1,
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleBrandFilter = (brand?: string) => {
    const newFilters = {
      ...filters,
      brand,
      page: 1,
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleFeaturedFilter = (isFeatured?: boolean) => {
    const newFilters = {
      ...filters,
      isFeatured,
      page: 1,
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const clearedFilters: PublicFilterDigitalProduct = {
      page: 1,
      limit: filters.limit || 10,
      productType: filters.productType, // Keep the current tab's product type
    };
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.minPrice ||
      filters.maxPrice ||
      filters.brand ||
      filters.isFeatured
    );
  }, [filters]);

  return (
    <>
      <div className="me-1 mt-3 pb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          {hasActiveFilters && (
            <button
              className="btn btn-sm btn-link text-danger text-xs p-0 fw-bold ms-auto"
              onClick={clearFilters}
              type="button"
              style={{ textDecoration: 'none' }}
            >
              Clear All
            </button>
          )}
        </div>

        
        <hr className="horizontal dark" />

        {/* Price Filter */}
        <b className="text-dark text-sm ms-2">Price</b>
        <div className="mt-2 ms-2">
          {[
            { id: "priceAllDig", label: "All", value: [undefined, undefined], active: "all" },
            { id: "priceUnder300Dig", label: "Under ₹300", value: [undefined, 300], active: "under300" },
            { id: "price300-500Dig", label: "₹300 - ₹500", value: [300, 500], active: "300-500" },
            { id: "price500-1000Dig", label: "₹500 - ₹1000", value: [500, 1000], active: "500-1000" },
            { id: "price1000-2000Dig", label: "₹1000 - ₹2000", value: [1000, 2000], active: "1000-2000" },
            { id: "price2000PlusDig", label: "₹2000 - More", value: [2000, undefined], active: "2000Plus" },
          ].map((f) => (
            <div className="form-check mb-0" key={f.id}>
              <input
                className="form-check-input"
                type="checkbox"
                name="priceFilter"
                id={f.id}
                checked={activePriceFilter === f.active}
                onChange={() => {
                  if (activePriceFilter === f.active && f.active !== "all") {
                    handlePriceFilter(undefined, undefined);
                  } else {
                    handlePriceFilter(f.value[0], f.value[1]);
                  }
                }}
              />
              <label
                className={`text-sm fw-normal text-dark ms-2 cursor-pointer ${
                  activePriceFilter === f.active ? 'text-primary fw-bold' : ''
                }`}
                htmlFor={f.id}
                style={{ cursor: 'pointer' }}
              >
                {f.label}
              </label>
            </div>
          ))}
        </div>

        <hr className="horizontal dark" />

        {/* Brand Filter */}
        <div className="mt-3 mb-3">
          <b className="text-dark text-sm ms-2">Brand</b>
          <div className="mt-2 ms-2">
            <div className="form-check mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                name="brandFilter"
                id="brandAllDig"
                checked={!filters.brand}
                onChange={() => handleBrandFilter(undefined)}
              />
              <label
                className={`text-sm fw-normal text-dark ms-2 cursor-pointer ${
                  !filters.brand ? 'text-primary fw-bold' : ''
                }`}
                htmlFor="brandAllDig"
                style={{ cursor: 'pointer' }}
              >
                All Brands
              </label>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .form-check-input:checked {
          background-color: #0d6efd;
        }
        .form-check-input:focus {
          border-color: #86b7fe;
          outline: 0;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        .form-check {
          transition: all 0.2s ease;
          padding: 4px 0;
        }
        .form-check:hover {
          background-color: rgba(13, 110, 253, 0.05);
          border-radius: 4px;
          padding: 4px 8px;
          margin-left: -8px;
          margin-right: -8px;
        }
        .form-check-input {
          cursor: pointer;
          margin-top: 0.25em;
          width: 0.95em;
          height: 0.95em;
        }
        .form-check-label {
          cursor: pointer;
        }
        .cursor-pointer {
          cursor: pointer;
          user-select: none;
        }
        .cursor-pointer:hover {
          color: #0d6efd !important;
        }
      `}</style>
    </>
  );
};

export default DigitalProductFilter;
